import { ReactNode } from 'react';
import classnames from 'classnames';
import styles from './text.module.sass';

type Element = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'text1' | 'text2' | 'text3' | 'text4' | 'text5';

type Alignment = 'start' | 'center' | 'end' | 'justify';

type FontWeight = 'regular' | 'medium' | 'bold';

type Color = 'white' | 'gray' | 'dark' | 'brand' | 'blue' | 'green' | 'red' | 'yellow' | 'orange';

type SizeValue =
    | '8'
    | '10'
    | '12'
    | '14'
    | '16'
    | '18'
    | '20'
    | '22'
    | '24'
    | '26'
    | '28'
    | '30'
    | '32'
    | '34'
    | '36'
    | '38'
    | '40'
    | '42'
    | '44'
    | '46'
    | '48';

type MarginValue = '0' | '1x' | '2x' | '3x' | '4x';

type Breakpoint = 'default' | 'md' | 'lg';

type ResponsiveMargin =
    | MarginValue
    | {
          [key in Breakpoint]?: MarginValue;
      };

type ResponsiveSize =
    | SizeValue
    | {
          [key in Breakpoint]?: SizeValue;
      };

const generateResponsiveClasses = (
    prefix: string,
    responsiveValue: Record<string, string | undefined> | string
): string => {
    if (typeof responsiveValue === 'string') {
        return styles[`${prefix}-${responsiveValue}`] || '';
    } else {
        return Object.entries(responsiveValue).reduce((classes, [breakpoint, value]) => {
            if (value) {
                const className = breakpoint === 'default' ? `${prefix}-${value}` : `${prefix}-${breakpoint}-${value}`;
                return `${classes} ${styles[className] || ''}`.trim();
            }
            return classes;
        }, '');
    }
};

export interface TextProps extends React.HTMLAttributes<HTMLDivElement> {
    /** The element type */
    as: Element;
    /** Text to display */
    children?: ReactNode;
    /** Adjust horizontal alignment of text */
    alignment?: Alignment;
    /** Prevent text from overflowing */
    breakWord?: boolean;
    /** Adjust color of text */
    color?: Color;
    /** Margin can be set as a pair mobile-pc-wide screen, or as a single value */
    margin?: ResponsiveMargin;
    /** Font-size can be set as a pair mobile-pc-wide screen, or as a single value */
    size?: ResponsiveSize;
    /** Adjust weight of text */
    fontWeight?: FontWeight;
    /** HTML id attribute */
    id?: string;
    /** Truncate text overflow with ellipsis */
    truncate?: boolean;
    /** Typographic style of text */
    variant?: Variant;
    className?: string;
}

export const Text = ({
    alignment = 'center',
    as,
    breakWord,
    children,
    color,
    margin,
    size,
    fontWeight,
    id,
    truncate = false,
    variant,
    className,
    ...props
}: TextProps) => {
    const Component = as;

    const classNames = classnames(
        variant && styles[variant],
        !variant && as === 'p' && styles.paragraph,
        fontWeight && styles[fontWeight],
        alignment && styles[alignment],
        breakWord && styles.break,
        color && styles[color],
        truncate && styles.truncate,
        margin && generateResponsiveClasses('margin', margin),
        size && generateResponsiveClasses('size', size),
        className
    );

    return (
        <Component className={classNames} {...(id && { id })} {...props}>
            {children}
        </Component>
    );
};
